import ChartFrame from './ChartFrame'
import DataTable from '../ui/DataTable'
import { formatPercent, sharePercent } from '../lib/format'
import { toOperators, type Operator } from '../lib/operators'
import type { DnsechoRow, IpDetails } from '@shared/api'
import styles from './OrgConcentration.module.css'

interface OrgConcentrationProps {
    rows: DnsechoRow[]
    self: IpDetails | undefined
}

interface Segment extends Operator {
    /** True for the folded "N others" bar, which represents several operators. */
    folded?: boolean
}

/**
 * Folds the canonical operator list into at most `max` bars for display, tail
 * collapsed into one "N others". This is a rendering concern only -- the true
 * operator count comes from toOperators and is reported separately, so the bar
 * never becomes the thing that counts.
 */
export function toSegments(operators: Operator[], max = 5): Segment[] {
    if (operators.length <= max) return operators

    const head = operators.slice(0, max - 1)
    const tail = operators.slice(max - 1)
    return [...head, {
        key: '__others__',
        name: `${tail.length} others`,
        count: tail.reduce((s, t) => s + t.count, 0),
        isSelf: tail.some((t) => t.isSelf),
        folded: true,
    }]
}

/**
 * The queries collapsed by operator, as one part-to-whole bar.
 *
 * This is the question visitors actually have. "Four resolver IPs" is not
 * meaningful; "two companies saw every domain you looked up, and one is your
 * ISP" is the finding. Large providers run thousands of relays, so counting
 * addresses systematically overstates how many parties were involved.
 *
 * The operator count comes from lib/operators -- the same function the verdict
 * uses -- so the headline number here can never disagree with the banner above
 * it. The aside reports the true distinct count, not the number of bars drawn:
 * with more than five operators the tail is folded into one "N others" segment,
 * but that is a drawing decision and must not change the count.
 *
 * Emphasis rather than categorical colour: one hue, stepped down in opacity so
 * the largest segment reads first. Four saturated hues would collapse under
 * deuteranopia, and every segment is labelled anyway, so identity never rests on
 * the colour. The hue lives in CSS -- only the step, which is data, is set here.
 */

/** Descending emphasis. Floors at 0.35 so the smallest slice stays visible. */
const emphasis = (index: number) => Math.max(0.35, 1 - index * 0.18)

export default function OrgConcentration({ rows, self }: OrgConcentrationProps) {
    const operators = toOperators(rows, self)
    const segments = toSegments(operators)
    const total = operators.reduce((sum, o) => sum + o.count, 0)
    const count = operators.length

    return (
        <ChartFrame
            title="Who saw your queries"
            aside={`${count} organisation${count === 1 ? '' : 's'}`}
            table={
                <DataTable
                    caption="Share of observed queries per operator"
                    columns={[
                        { key: 'name', header: 'Operator', render: (o: Operator) => o.name },
                        { key: 'count', header: 'Queries', numeric: true, render: (o: Operator) => o.count },
                        { key: 'share', header: 'Share', numeric: true,
                          render: (o: Operator) => formatPercent(sharePercent(o.count, total)) },
                    ]}
                    rows={operators}
                    rowKey={(o) => o.key}
                />
            }
        >
            <div className={styles.bar}>
                {segments.map((seg, i) => (
                    <span
                        key={seg.key}
                        className={styles.segment}
                        style={{
                            width: `${sharePercent(seg.count, total)}%`,
                            opacity: emphasis(i),
                        }}
                    />
                ))}
            </div>

            <ul className={styles.legend}>
                {segments.map((seg, i) => (
                    <li key={seg.key} className={styles.item}>
                        <span
                            className={styles.swatch}
                            style={{ opacity: emphasis(i) }}
                            aria-hidden="true"
                        />
                        <span>{seg.name}</span>
                        <span className={styles.pct}>{formatPercent(sharePercent(seg.count, total))}</span>
                        {seg.isSelf && <span className={styles.warn}>your ISP</span>}
                    </li>
                ))}
            </ul>
        </ChartFrame>
    )
}
